import type { DomainWithMetadata, NormalizedDomain } from 'ag-charts-core';
import { Color, clamp, toNumber } from 'ag-charts-core';
import type { AgNumericValue } from 'ag-charts-types';

import { AbstractScale } from './abstractScale';
import { Invalidating } from './invalidating';
import { unpackDomainMinMax } from './scaleUtil';

type OKLCHA = { l: number; c: number; h: number; a: number };

const convertColorStringToOklcha = (v: string): OKLCHA => {
    const color = Color.fromString(v);
    const [l, c, h] = Color.RGBtoOKLCH(color.r, color.g, color.b);
    return { l, c, h, a: color.a };
};

const delta = 1e-6;
const MAX_CONVERT_CACHE_SIZE = 4096;
const isAchromatic = (x: OKLCHA) => x.c < delta || x.l < delta || x.l > 1 - delta;
const interpolateOklch = (x: OKLCHA, y: OKLCHA, d: number): Color => {
    d = clamp(0, d, 1);

    let h: number;
    if (isAchromatic(x)) {
        h = y.h;
    } else if (isAchromatic(y)) {
        h = x.h;
    } else {
        const xH = x.h;
        let yH = y.h;
        const deltaH = y.h - x.h;
        if (deltaH > 180) {
            yH -= 360;
        } else if (deltaH < -180) {
            yH += 360;
        }
        h = xH * (1 - d) + yH * d;
    }

    const c = x.c * (1 - d) + y.c * d;
    const l = x.l * (1 - d) + y.l * d;
    const a = x.a * (1 - d) + y.a * d;

    return Color.fromOKLCH(l, c, h, a);
};

export class ColorScale extends AbstractScale<number, string> {
    readonly type = 'color';
    readonly defaultTickCount = 0;
    protected invalid = true;

    @Invalidating
    domain = [0, 1];
    @Invalidating
    range = ['red', 'blue'];
    @Invalidating
    mode: 'continuous' | 'discrete' = 'continuous';
    /**
     * User-visible axis domain for the gradient legend. Decoupled from
     * `domain` (which carries interpolation pivots) so that colour-stop
     * positions do not distort the legend axis range.
     */
    displayDomain?: [AgNumericValue, AgNumericValue];

    private parsedRange = this.range.map(convertColorStringToOklcha);

    // OPTIMIZATION: OKLCH interpolation and RGBA-string allocation dominate heat-map redraws, where
    // the same value recurs across cells and frames. Cleared in update() on any domain/range/mode
    // change. At the cap we stop inserting rather than clearing, which would thrash mid-redraw.
    private readonly convertCache = new Map<number, string>();

    update() {
        this.convertCache.clear();
        const { domain, range } = this;

        if (domain.length < 2) {
            this.logger.warnOnce('`colorDomain` should have at least 2 values.');
            if (domain.length === 0) {
                domain.push(0, 1);
            } else if (domain.length === 1) {
                domain.push(domain[0] + 1);
            }
        }

        for (let i = 1; i < domain.length; i++) {
            const a = domain[i - 1];
            const b = domain[i];
            if (a > b) {
                this.logger.warnOnce('`colorDomain` values should be supplied in ascending order.');
                domain.sort((a2, b2) => a2 - b2);
                break;
            }
        }

        // For discrete mode, domain may have N+1 boundaries for N colours.
        const expectedLength = this.mode === 'discrete' ? domain.length - 1 : domain.length;
        if (range.length < expectedLength) {
            for (let i = range.length; i < expectedLength; i++) {
                range.push(range.length > 0 ? range[0] : 'black');
            }
        }

        this.parsedRange = this.range.map(convertColorStringToOklcha);
    }

    override normalizeDomains(...domains: DomainWithMetadata<number>[]): NormalizedDomain<number> {
        return { domain: domains.map((d) => d.domain).flat(), animatable: true };
    }

    override toDomain(): number | undefined {
        return;
    }

    convert(x: AgNumericValue) {
        this.refresh();

        // Colour derives from the value's position in the Number domain, so finite precision suffices.
        const xn = toNumber(x);

        // Lookup sits after refresh() so a reconfigured scale never serves a stale colour.
        const cached = this.convertCache.get(xn);
        if (cached !== undefined) {
            return cached;
        }

        const result = this.computeColor(xn);
        if (this.convertCache.size < MAX_CONVERT_CACHE_SIZE) {
            this.convertCache.set(xn, result);
        }
        return result;
    }

    private computeColor(xn: number): string {
        const { domain, range, parsedRange } = this;
        const d0 = domain[0];
        const d1 = domain.at(-1)!;
        const r0 = range[0];
        const r1 = range.at(-1)!;

        if (xn <= d0) {
            return r0;
        }

        if (xn >= d1) {
            return r1;
        }

        let index: number;
        let q: number;

        if (domain.length === 2) {
            const t = (xn - d0) / (d1 - d0);
            const step = 1 / (range.length - 1);
            index = range.length <= 2 ? 0 : Math.min(Math.floor(t * (range.length - 1)), range.length - 2);
            q = (t - index * step) / step;
        } else {
            for (index = 0; index < domain.length - 2; index++) {
                if (xn < domain[index + 1]) {
                    break;
                }
            }
            const a = domain[index];
            const b = domain[index + 1];
            q = (xn - a) / (b - a);
        }

        if (this.mode === 'discrete') {
            return range[index];
        }

        const c0 = parsedRange[index];
        const c1 = parsedRange[index + 1];
        return interpolateOklch(c0, c1, q).toRgbaString();
    }

    override invert(): number | undefined {
        return;
    }

    override getDomainMinMax() {
        return unpackDomainMinMax(this.domain);
    }

    protected refresh() {
        if (!this.invalid) return;

        this.invalid = false;
        this.update();

        if (this.invalid) {
            this.logger.warnOnce('Expected update to not invalidate scale');
        }
    }
}
